import React, { useEffect, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import CircularProgress from "@material-ui/core/CircularProgress";
import ThumbUpAltIcon from "@material-ui/icons/ThumbUpAlt";
import ThumbDownAltIcon from "@material-ui/icons/ThumbDownAlt";

const LIKE_COLOR = "#639922";
const DISLIKE_COLOR = "#E24B4A";

const formatDuration = (sec) => {
  if (sec == null || isNaN(sec)) return "";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const formatListDate = (s) => {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const parseReasons = (s) => {
  if (!s) return [];
  return s
    .split("||")
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => p.replace(/^\d+\s*[-.)]\s*/, ""));
};

// Compact like/dislike split bar for the modal header.
const MiniBar = ({ likes, dislikes }) => {
  const total = likes + dislikes;
  const likePct = total > 0 ? Math.round((likes / total) * 100) : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div
        style={{
          width: 160,
          height: 12,
          borderRadius: 6,
          overflow: "hidden",
          display: "flex",
          backgroundColor: "#e5e7eb",
        }}
      >
        <div style={{ width: `${likePct}%`, backgroundColor: LIKE_COLOR }} />
        <div style={{ width: `${100 - likePct}%`, backgroundColor: DISLIKE_COLOR }} />
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, color: "#374151" }}>
        {likePct}% beğeni
      </span>
    </div>
  );
};

const ReactionBadge = ({ type }) => {
  const like = type === 1;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "2px 10px",
        borderRadius: 12,
        fontSize: 12,
        fontWeight: 600,
        backgroundColor: like ? "#eaf6e3" : "#fdeaea",
        color: like ? "#3B6D11" : "#A32D2D",
      }}
    >
      {like ? (
        <ThumbUpAltIcon style={{ fontSize: 14 }} />
      ) : (
        <ThumbDownAltIcon style={{ fontSize: 14 }} />
      )}
      {like ? "Beğendi" : "Beğenmedi"}
    </span>
  );
};

const ReactionVideoModal = ({ isOpen, onClose, row, detail, loading }) => {
  const videoRef = useRef(null);
  const pendingSeekRef = useRef(null);
  const [videoTab, setVideoTab] = useState("mp4");
  const [videoError, setVideoError] = useState(false);
  const [videoDuration, setVideoDuration] = useState(null);

  // Reset player state whenever a different video opens. The detail payload has no
  // id of its own, so key off the row's AchievementVideoId.
  useEffect(() => {
    setVideoTab("mp4");
    setVideoError(false);
    setVideoDuration(null);
    pendingSeekRef.current = null;
  }, [row?.AchievementVideoId]);

  // Switch MP4 <-> HLS while keeping position/play-state (single <video> element).
  const switchVideoTab = (tab) => {
    if (tab === videoTab || !detail) return;
    if (videoRef.current) {
      pendingSeekRef.current = {
        time: videoRef.current.currentTime,
        playing: !videoRef.current.paused,
      };
    }
    setVideoError(false);
    setVideoTab(tab);
  };

  const openInNewTab = () => {
    if (!detail?.VideoUrl) return;
    const w = window.open(detail.VideoUrl, "_blank", "noopener,noreferrer");
    if (w) w.opener = null;
  };

  const likes = row?.Likes ?? 0;
  const dislikes = row?.Dislike ?? 0;
  const total = row?.TotalReactions ?? likes + dislikes;
  const durationStr = videoDuration ? formatDuration(videoDuration) : "";

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{ minWidth: 0 }}>
            <div style={styles.title}>
              Video #{row?.AchievementVideoId}
            </div>
            <div style={styles.subtitle}>
              Kazanım {row?.AchievementId}
              {durationStr ? ` · süre ${durationStr}` : ""}
            </div>
          </div>
          <button onClick={onClose} style={styles.closeBtn} aria-label="Kapat">
            ✕
          </button>
        </div>

        {loading || !detail ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 60 }}>
            <CircularProgress />
          </div>
        ) : (
          <>
            {/* Video player */}
            <div style={styles.videoTabBar}>
              <button
                type="button"
                onClick={() => switchVideoTab("mp4")}
                style={videoTab === "mp4" ? styles.videoTabActive : styles.videoTab}
              >
                MP4
              </button>
              <button
                type="button"
                disabled={!detail.HlsUrl}
                onClick={() => switchVideoTab("hls")}
                title={detail.HlsUrl ? "HLS" : "Bu video için HLS mevcut değil"}
                style={
                  !detail.HlsUrl
                    ? styles.videoTabDisabled
                    : videoTab === "hls"
                    ? styles.videoTabActive
                    : styles.videoTab
                }
              >
                HLS
              </button>
              <button onClick={openInNewTab} style={styles.openLink}>
                ↗ Yeni sekmede aç
              </button>
            </div>
            <video
              key={videoTab}
              ref={videoRef}
              src={
                videoTab === "hls" && detail.HlsUrl
                  ? detail.HlsUrl
                  : detail.VideoUrl
              }
              controls
              onError={() => setVideoError(true)}
              onLoadedMetadata={() => {
                setVideoError(false);
                if (videoRef.current && !isNaN(videoRef.current.duration)) {
                  setVideoDuration(videoRef.current.duration);
                }
                const pending = pendingSeekRef.current;
                if (pending && videoRef.current) {
                  videoRef.current.currentTime = pending.time;
                  if (pending.playing) videoRef.current.play().catch(() => {});
                  pendingSeekRef.current = null;
                }
              }}
              style={{ width: "100%", maxHeight: 420, backgroundColor: "#000" }}
            />
            {videoTab === "hls" && videoError && (
              <div style={{ color: "red", fontSize: 12, padding: "4px 8px" }}>
                HLS bu tarayıcıda oynatılamadı. (HLS yalnızca Safari'de yerel olarak
                desteklenir.)
              </div>
            )}

            {/* Reaction summary */}
            <div style={styles.summary}>
              <span style={{ color: "#15803d", fontWeight: 600 }}>
                <ThumbUpAltIcon style={{ fontSize: 16, verticalAlign: "-3px" }} /> {likes}
              </span>
              <span style={{ color: "#b91c1c", fontWeight: 600 }}>
                <ThumbDownAltIcon style={{ fontSize: 16, verticalAlign: "-3px" }} /> {dislikes}
              </span>
              <span style={{ color: "#6b7280" }}>Toplam {total}</span>
              <div style={{ marginLeft: "auto" }}>
                <MiniBar likes={likes} dislikes={dislikes} />
              </div>
            </div>

            {/* Per-user reactions */}
            <div style={styles.listHeader}>
              <span style={styles.colUser}>Kullanıcı</span>
              <span style={styles.colReaction}>Tepki</span>
              <span style={styles.colDesc}>Açıklama</span>
              <span style={styles.colDate}>Tarih</span>
            </div>
            <div style={styles.listBody}>
              {detail.UserReactions && detail.UserReactions.length > 0 ? (
                detail.UserReactions.map((r, i) => {
                  const reasons = parseReasons(r.Description1);
                  const comment =
                    r.Description2 && r.Description2.trim()
                      ? r.Description2.trim()
                      : null;
                  return (
                    <div key={`${r.UserId}-${i}`} style={styles.listRow}>
                      <span style={styles.colUser}>{r.UserId}</span>
                      <span style={styles.colReaction}>
                        <ReactionBadge type={r.ReactionType} />
                      </span>
                      <span style={styles.colDesc}>
                        {reasons.length === 0 && !comment ? (
                          <span style={{ color: "#c0c0c0" }}>—</span>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {reasons.length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                                {reasons.map((reason, idx) => (
                                  <span key={idx} style={styles.reasonChip}>
                                    {reason}
                                  </span>
                                ))}
                              </div>
                            )}
                            {comment && (
                              <div style={styles.comment}>“{comment}”</div>
                            )}
                          </div>
                        )}
                      </span>
                      <span style={styles.colDate}>{formatListDate(r.CreatedDate)}</span>
                    </div>
                  );
                })
              ) : (
                <div style={{ padding: 20, textAlign: "center", color: "#9ca3af" }}>
                  Bu video için tepki bulunamadı.
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

const styles = {
  container: { padding: 0, minWidth: 320 },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    padding: "16px 20px",
    borderBottom: "1px solid #e5e7eb",
  },
  title: { fontSize: 17, fontWeight: 700, color: "#111827" },
  subtitle: { fontSize: 13, color: "#6b7280", marginTop: 2 },
  closeBtn: {
    flexShrink: 0,
    border: "none",
    background: "transparent",
    fontSize: 18,
    color: "#6b7280",
    cursor: "pointer",
    lineHeight: 1,
  },
  videoTabBar: { display: "flex", alignItems: "center", gap: 4, padding: "10px 12px 0 12px" },
  videoTab: {
    padding: "6px 16px",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "transparent",
    cursor: "pointer",
    fontSize: 14,
    color: "#5f6368",
  },
  videoTabActive: {
    padding: "6px 16px",
    border: "none",
    borderBottom: "2px solid #2563eb",
    background: "transparent",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    color: "#2563eb",
  },
  videoTabDisabled: {
    padding: "6px 16px",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "transparent",
    cursor: "not-allowed",
    fontSize: 14,
    color: "#bdbdbd",
  },
  openLink: {
    marginLeft: "auto",
    border: "none",
    background: "transparent",
    color: "#2563eb",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600,
  },
  summary: {
    display: "flex",
    alignItems: "center",
    gap: 18,
    padding: "12px 20px",
    borderTop: "1px solid #f0f0f0",
    borderBottom: "1px solid #e5e7eb",
    fontSize: 14,
    flexWrap: "wrap",
  },
  listHeader: {
    display: "flex",
    padding: "10px 20px",
    fontSize: 12,
    fontWeight: 700,
    color: "#6b7280",
    backgroundColor: "#f9fafb",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  listBody: { maxHeight: 280, overflowY: "auto" },
  listRow: {
    display: "flex",
    alignItems: "flex-start",
    padding: "10px 20px",
    borderBottom: "1px solid #f0f0f0",
    fontSize: 13,
  },
  colUser: { width: 90, flexShrink: 0, color: "#374151" },
  colReaction: { width: 120, flexShrink: 0 },
  colDesc: { flex: 1, color: "#4b5563", minWidth: 0, paddingRight: 8 },
  colDate: { width: 130, flexShrink: 0, color: "#6b7280", fontSize: 12, textAlign: "right" },
};

export default ReactionVideoModal;
